import { useTranslation } from 'next-i18next';
import { ComponentProps, FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, ControllerFieldState, ControllerRenderProps } from 'react-hook-form';
import { Box, ButtonV2, RadioToggleV2, DropdownV2, TextArea } from '@bridebook/ui';
import {
  useRsvpForms,
  useRsvpData,
  useRsvpNavigation,
  useRsvpState,
} from '../../guest-rsvp.context';
import GuestRsvpHeader from '../../partials/header';
import componentStyles from '../substep.style';
import { getThemeProperties } from '@bridebook/web/lib/wedding-website/utils/get-theme-properties';
import SubmitButtonDesktopWrapper from '../../partials/submit-button-desktop-wrapper';
import SubmitButtonMobileWrapper from '../../partials/submit-button-mobile-wrapper';

interface Question {
  id: string;
  question: string;
  type: 'boolean' | 'list' | 'string';
  options?: string[];
}

interface IContinueButtonProps {
  styleWrapper: ComponentProps<typeof ButtonV2>['styleWrapper'];
  onClick: ComponentProps<typeof ButtonV2>['onClick'];
  disabled: ComponentProps<typeof ButtonV2>['disabled'];
  children: string;
}

const ContinueButton: FC<IContinueButtonProps> = ({
  styleWrapper,
  onClick,
  disabled,
  children,
}) => (
  <ButtonV2 styleWrapper={styleWrapper} onClick={onClick} disabled={disabled}>
    {children}
  </ButtonV2>
);

const QuestionsStep: FC = () => {
  const { t } = useTranslation('weddingWebsite');
  const { questionsForm } = useRsvpForms();
  const { setStep, currentQuestionIndex, setCurrentQuestionIndex } = useRsvpNavigation();
  const { weddingRsvp, theme } = useRsvpData();
  const { isPreview } = useRsvpState();

  const themeProperties = useMemo(() => getThemeProperties(theme), [theme]);
  const styles = useMemo(
    () => componentStyles(themeProperties, isPreview),
    [themeProperties, isPreview],
  );

  // Get questions that are registered in the form
  const registeredQuestionIds = Object.keys(questionsForm.getValues() || {});

  // Find the matching questions from weddingRsvp
  const questionsToShow: Question[] =
    weddingRsvp?.questions?.filter((question) => registeredQuestionIds.includes(question.id)) || [];

  // Current question
  const currentQuestion = questionsToShow[currentQuestionIndex];

  const [currentQuestionValid, setCurrentQuestionValid] = useState<boolean>(false);

  const updateQuestionValidity = useCallback(
    (questionId: string) => {
      questionsForm.trigger(questionId).then((isValid) => {
        setCurrentQuestionValid(isValid);
      });
    },
    [questionsForm],
  );

  const handleQuestionFieldChange = useCallback(
    (questionId: string, value: string | boolean, onChange: (value: string | boolean) => void) => {
      onChange(value);
      updateQuestionValidity(questionId);
    },
    [updateQuestionValidity],
  );

  useEffect(() => {
    if (currentQuestion) {
      // Validate the current question when it changes
      updateQuestionValidity(currentQuestion.id);
    }
  }, [currentQuestion, updateQuestionValidity]);

  // If no questions are registered, skip to the note step
  useEffect(() => {
    if (!registeredQuestionIds.some((id) => id !== 'note')) {
      setStep('RSVP_FORM.NOTE');
    }
  }, [registeredQuestionIds, setStep]);

  const handleContinue: ComponentProps<typeof ButtonV2>['onClick'] = () => {
    if (currentQuestionIndex < questionsToShow.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setStep('RSVP_FORM.NOTE');
    }
  };

  const renderQuestion = useCallback(
    (question: Question) => {
      const renderFieldContent = (
        field: ControllerRenderProps,
        fieldState: ControllerFieldState,
      ) => {
        switch (question.type) {
          case 'boolean':
            return (
              <Box style={styles.questions.answerOptionsWrapper}>
                <RadioToggleV2
                  id={`${question.id}-yes`}
                  name={question.id}
                  value="true"
                  checked={field.value === true}
                  label={t('rsvp.guest.common.yes')}
                  wrapperStyle={styles.questions.answerOption}
                  onChange={() => {
                    handleQuestionFieldChange(question.id, true, field.onChange);
                    field.onBlur();
                  }}
                />
                <RadioToggleV2
                  id={`${question.id}-no`}
                  name={question.id}
                  value="false"
                  checked={field.value === false}
                  label={t('rsvp.guest.common.no')}
                  wrapperStyle={styles.questions.answerOption}
                  onChange={() => {
                    handleQuestionFieldChange(question.id, false, field.onChange);
                    field.onBlur();
                  }}
                />
              </Box>
            );
          case 'list':
            return (
              <DropdownV2
                name={question.id}
                selected={field.value || ''}
                fullWidth
                maxLines={6}
                placeholder={t('rsvp.guest.questions.list.Placeholder')}
                value={field.value || ''}
                onSelect={({ value }) => {
                  handleQuestionFieldChange(question.id, value, field.onChange);
                  field.onBlur();
                }}
                options={
                  question.options?.map((option: string) => ({
                    value: option,
                    label: option,
                  })) || []
                }
              />
            );
          case 'string':
            return (
              <>
                <TextArea
                  name={question.id}
                  id={question.id}
                  label={t('rsvp.guest.questions.string.label')}
                  placeholder={t('rsvp.guest.questions.string.placeholder')}
                  autoFocus={false}
                  controlled
                  value={field.value || ''}
                  onChange={(value) => {
                    handleQuestionFieldChange(question.id, value, field.onChange);
                  }}
                  onBlur={field.onBlur}
                  error={fieldState.isTouched && fieldState.error?.message}
                  withAdjustableHeight
                  rows={1}
                  innerWrapperStyle={{ minHeight: 'auto' }}
                />
              </>
            );
          default:
            return <Box />;
        }
      };

      return (
        <Controller
          key={`controller-${question.id}`}
          control={questionsForm.control}
          name={question.id}
          defaultValue={question.type === 'boolean' ? undefined : ''}
          render={({ field, fieldState }) => renderFieldContent(field, fieldState)}
        />
      );
    },
    [
      questionsForm.control,
      handleQuestionFieldChange,
      t,
      styles.questions.answerOptionsWrapper,
      styles.questions.answerOption,
    ],
  );

  return (
    <>
      <Box style={styles.substepWrapper}>
        <GuestRsvpHeader />
        <Box style={styles.questions.questionWrapper}>
          {currentQuestion && (
            <>
              <Box as="label" htmlFor={currentQuestion.id} style={styles.questions.question}>
                {currentQuestion.question}
              </Box>
              {renderQuestion(currentQuestion)}
            </>
          )}
        </Box>
        <SubmitButtonDesktopWrapper>
          <ContinueButton
            styleWrapper={styles.continueButton}
            onClick={handleContinue}
            disabled={!currentQuestionValid}>
            {t('rsvp.guest.common.continue')}
          </ContinueButton>
        </SubmitButtonDesktopWrapper>
      </Box>
      <SubmitButtonMobileWrapper>
        <ContinueButton
          styleWrapper={styles.continueButton}
          onClick={handleContinue}
          disabled={!currentQuestionValid}>
          {t('rsvp.guest.common.continue')}
        </ContinueButton>
      </SubmitButtonMobileWrapper>
    </>
  );
};

export default QuestionsStep;
